// Ledger → Billing Summaries view (rebuilt 2026-05-25).
//
// Three-level hierarchy, newest first:
//
//   MONTH (e.g. "MAY 2026")
//     └── PROJECT (e.g. "26-Hoback")
//         ├── Monthly BS rows (BS-2026-05-Hoback)
//         └── Off-cycle proposal rows (Approved + deposit invoice
//             + billed_on_summary_id IS NULL, approved within the
//             same month)
//
// Filter bar: Project multi-select, Period radio, Status radio, search.
// URL hash filter persistence (namespace 'bs').
//
// Row expansion (multi-row): URL-hash-tracked open IDs survive refresh.
//   - Monthly BS expanded: Design Hours / Proposals / Reimbursable
//     Receipts / Credits — each section uses the simplified-cell
//     pattern (AmountCell + hover tooltip) shared with the Procurement
//     view. The Proposals subsection embeds a proposal card per
//     bundled proposal, with the same 9-column items table layout.
//   - Off-cycle proposal expanded: just the proposal card.
//
// Data: billing_summaries + hours_invoices loaded directly via
// supabase (neither in AppContext yet). Everything else flows from
// AppContext: proposals / invoices / items / paymentAllocations /
// clientPayments / vendorPayments / purchaseOrders / receipts /
// changeOrders / clientCreditLedger / projects.

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAppContext } from '../AppContext';
import {
  billingSummaryRollup, procurementRowState, offCycleProposalsForMonth,
  itemTotalProfit, isBillable,
} from '../../utils';
import { jumpTo } from '../../lib/jump';
import { useHashFilters } from '../../lib/useHashFilters';
import LedgerHoverTooltip from './LedgerHoverTooltip';
import LedgerCoBadge from './LedgerCoBadge';
import {
  // Constants
  GROUP_BG, HEADER_BG, ROW_BG_A, ROW_BG_B, FOOTER_BG, CARD_BORDER, CARD_RADIUS,
  W, td, th, thRight,
  // Formatters / layout helpers
  fmt$, fmtShortDate, cellWidth, stickyLeft, stickyRight, ColGroup,
  // Components
  TooltipHeading, TooltipRow, AmountCell, FeesCell, PaymentBlocks,
  // Classifiers
  depositState, finalState, poState, venFinalState, amountStatusColor, statusLabel,
  // Payment-block lookups
  clientPaymentBlocksFor, vendorPaymentBlocksFor,
} from './LedgerShared';

// ── Period label helpers ────────────────────────────────────────────────
var MONTHS_LONG  = ['', 'JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];

function monthKeyFor(dateStr) {
  // YYYY-MM key. Falls back to '' so unparseable rows sort last.
  if (!dateStr) return '';
  var s = String(dateStr).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(s) ? s : '';
}
function monthHeadingFor(key) {
  // 'YYYY-MM' → 'MAY 2026'
  if (!key) return 'Unknown period';
  var parts = key.split('-');
  return (MONTHS_LONG[parseInt(parts[1], 10)] || '') + ' ' + parts[0];
}

// ── Period filter encoding ──────────────────────────────────────────────
// Matches the proc.date convention from LedgerProcurement.
function periodRange(value) {
  if (!value) return null;
  var now = new Date();
  function iso(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + dd;
  }
  if (value === 'thismonth') {
    return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: null };
  }
  if (value === 'lastmonth') {
    var lmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    var lmEnd   = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: iso(lmStart), end: iso(lmEnd) };
  }
  if (value === 'last6m') {
    return { start: iso(new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())), end: null };
  }
  if (value === 'lastyear') {
    return { start: iso(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())), end: null };
  }
  return null;
}

// ── BS payment status derivation ────────────────────────────────────────
function bsPaymentStatus(rollup) {
  var issued = rollup.totals.issued || 0;
  var paid   = rollup.totals.paid   || 0;
  if (issued <= 0.01) return 'empty';
  if (paid >= issued - 0.01) return 'paid';
  if (paid > 0.01)           return 'partial';
  return 'unpaid';
}

// ── Button row component (project / period / status filters) ────────────
function FilterRow(p) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 9, fontWeight: 600, color: '#888',
        textTransform: 'uppercase', letterSpacing: '0.07em', minWidth: 60 }}>
        {p.label}
      </span>
      {p.options.map(function(opt) {
        var active = p.multi
          ? (p.value || []).indexOf(opt.value) >= 0
          : p.value === opt.value;
        return (
          <button key={opt.value}
            onClick={function() { p.onChange(opt.value); }}
            style={{
              fontSize: 11, padding: '4px 10px',
              border: '0.5px solid ' + (active ? '#111' : '#D8D7D2'),
              background: active ? '#111' : '#fff',
              color: active ? '#fff' : '#444',
              borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function LedgerBillingSummaries(p) {
  var ctx = useAppContext();
  var firmSettings = p.firmSettings || ctx.firmSettings || {};

  // ── Source data from AppContext ──────────────────────────────────────
  var items              = ctx.items              || [];
  var proposals          = ctx.proposals          || [];
  var invoices           = ctx.invoices           || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var vendorPayments     = ctx.vendorPayments     || [];
  var purchaseOrders     = ctx.purchaseOrders     || [];
  var clientPayments     = ctx.clientPayments     || [];
  var changeOrders       = ctx.changeOrders       || [];
  var receipts           = ctx.receipts           || [];
  var clientCreditLedger = ctx.clientCreditLedger || [];
  var projects           = ctx.projects           || [];

  var setProposals      = ctx.setProposals;
  var setInvoices       = ctx.setInvoices;
  var setPurchaseOrders = ctx.setPurchaseOrders;

  // ── Direct-load: billing_summaries + hours_invoices ──────────────────
  var [summaries, setSummaries] = useState([]);
  var [hoursInvoices, setHoursInvoices] = useState([]);
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState('');

  useEffect(function() {
    var cancelled = false;
    setLoading(true); setLoadError('');
    (async function() {
      var bs = await supabase.from('billing_summaries')
        .select('*')
        .order('sent_at', { ascending: false, nullsFirst: true })
        .order('created_at', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (bs.error) { setLoadError(bs.error.message); setLoading(false); return; }
      // sent_at column doesn't exist on hours_invoices in production
      // (the canonical buildHoursInvoiceRow in DesignerHoursSection.jsx
      // writes 13 columns and sent_at isn't among them; a write at
      // BillingSummarySection.jsx:1718 appears to silently fail — a
      // pre-existing schema/code drift, not in scope here). The hours
      // invoice ships AS PART OF the parent billing summary, so we use
      // bs.sent_at as the "Payment request sent" date in the Design
      // Hours section instead — same semantics, real column.
      var hi = await supabase.from('hours_invoices')
        .select('invoice_number, project_label, status, total_amount, invoice_date')
        .order('invoice_date', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (hi.error) { setLoadError(hi.error.message); setLoading(false); return; }
      setSummaries(bs.data || []);
      setHoursInvoices(hi.data || []);
      setLoading(false);
    })();
    return function() { cancelled = true; };
  }, []);

  // ── Filters (URL hash, namespace 'bs') ───────────────────────────────
  var f = useHashFilters({
    namespace: 'bs',
    defaults: { proj: '', period: '', status: 'all', q: '', open: '' },
  });
  var filters = f[0]; var setFilter = f[2];
  // proj is comma-separated for multi-select; empty = all
  var projList = (filters.proj || '').split(',').filter(Boolean);
  var periodFlt = filters.period;
  var statusFlt = filters.status;
  var query     = filters.q;
  // Open row IDs (comma-separated for persistence via URL)
  var openIds   = (filters.open || '').split(',').filter(Boolean);
  var openSet = useMemo(function() {
    var s = {};
    openIds.forEach(function(id) { s[id] = true; });
    return s;
  }, [filters.open]);

  function toggleOpen(id) {
    var current = (filters.open || '').split(',').filter(Boolean);
    var idx = current.indexOf(id);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(id);
    setFilter('open', current.join(','));
  }

  function toggleProject(label) {
    if (label === '') { setFilter('proj', ''); return; }
    var current = (filters.proj || '').split(',').filter(Boolean);
    var idx = current.indexOf(label);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(label);
    setFilter('proj', current.join(','));
  }

  // ── Project list (for filter pills) ──────────────────────────────────
  var allProjectLabels = useMemo(function() {
    var s = {};
    summaries.forEach(function(bs) { if (bs.project_label) s[bs.project_label] = true; });
    proposals.forEach(function(pp) { if (pp.project) s[pp.project] = true; });
    projects.forEach(function(pr) { if (pr.label) s[pr.label] = true; });
    return Object.keys(s).sort();
  }, [summaries, proposals, projects]);

  // ── Build rollup ctx shared by all bs rows ──────────────────────────
  var bsCtx = useMemo(function() {
    return {
      proposals: proposals, invoices: invoices,
      paymentAllocations: paymentAllocations, payments: clientPayments,
      receipts: receipts, hoursInvoices: hoursInvoices, items: items,
    };
  }, [proposals, invoices, paymentAllocations, clientPayments, receipts, hoursInvoices, items]);

  // ── Hierarchy: month → project → rows ────────────────────────────────
  var months = useMemo(function() {
    if (loading) return [];
    var q = (query || '').trim().toLowerCase();
    var range = periodRange(periodFlt);

    // Step 1: build BS rows with rollup data + month key.
    var bsRows = summaries.map(function(bs) {
      var rollup = billingSummaryRollup(bs, bsCtx) || {
        designerHoursInvoice: null, proposals: [], reimbursableReceipts: [],
        totals: { issued: 0, paid: 0, outstanding: 0 },
      };
      // Period for grouping: prefer year/month columns (explicit), else
      // fall back to sent_at month.
      var periodKey;
      if (bs.year && bs.month) {
        periodKey = String(bs.year) + '-' + String(bs.month).padStart(2, '0');
      } else {
        periodKey = monthKeyFor(bs.sent_at);
      }
      return {
        kind:       'bs',
        id:         bs.id,
        bs:         bs,
        rollup:     rollup,
        project:    bs.project_label || '',
        sentAt:     bs.sent_at || '',
        monthKey:   periodKey,
        status:     bs.status || 'draft',
        paymentStatus: bsPaymentStatus(rollup),
      };
    });

    // Step 2: build off-cycle proposal rows. Walk every distinct month
    // that has any BS row OR any off-cycle proposal — bs rows give us
    // the months we care about; for completeness we also collect months
    // that hold only off-cycle proposals (project with no BS that month).
    var distinctMonths = {};
    bsRows.forEach(function(r) { if (r.monthKey) distinctMonths[r.monthKey] = true; });
    // Also scan all approved proposals' months to surface off-cycle-
    // only months (a project might have an off-cycle proposal without
    // any BS in that month).
    proposals.forEach(function(pp) {
      if (pp.status !== 'Approved') return;
      var billed = pp.billedOnSummaryId || pp.billed_on_summary_id || '';
      if (billed) return;
      var mk = monthKeyFor(pp.approved_at || pp.approvedAt);
      if (mk) distinctMonths[mk] = true;
    });
    var offCycleRows = [];
    Object.keys(distinctMonths).forEach(function(mk) {
      var parts = mk.split('-');
      var ocs = offCycleProposalsForMonth(parseInt(parts[0], 10), parseInt(parts[1], 10), proposals);
      ocs.forEach(function(pp) {
        // Resolve the deposit invoice for this proposal (so the
        // collapsed row can surface its amount + status). The deposit
        // invoice is created at approveProposal time.
        var depInv = invoices.find(function(inv) {
          return inv.proposalId === pp.id && (inv.invoiceType === 'deposit' || inv.invoice_type === 'deposit');
        }) || null;
        var amtDue = depInv ? (parseFloat(depInv.amountDue) || 0) : 0;
        offCycleRows.push({
          kind:        'offcycle',
          id:          'oc:' + pp.id,
          proposal:    pp,
          depositInv:  depInv,
          project:     pp.project || '',
          monthKey:    mk,
          approvedAt:  pp.approved_at || pp.approvedAt || '',
          totalIssued: amtDue,
        });
      });
    });

    // Step 3: filter against the filter bar.
    function passes(row) {
      // Project filter (multi-select; empty = all)
      if (projList.length > 0 && projList.indexOf(row.project) < 0) return false;
      // Period filter (on sent_at for BS, approved_at for off-cycle)
      if (range) {
        var d = row.kind === 'bs'
          ? String(row.sentAt || '').slice(0, 10)
          : String(row.approvedAt || '').slice(0, 10);
        if (!d) return false;
        if (range.start && d < range.start) return false;
        if (range.end   && d > range.end)   return false;
      }
      // Status filter
      if (statusFlt !== 'all') {
        if (row.kind === 'bs') {
          if (statusFlt === 'outstanding' && row.rollup.totals.outstanding <= 0.01) return false;
          if (statusFlt === 'paid'    && row.paymentStatus !== 'paid')    return false;
          if (statusFlt === 'partial' && row.paymentStatus !== 'partial') return false;
        } else {
          // Off-cycle rows treat the deposit invoice status as the row's
          // payment status when filtered.
          if (statusFlt === 'paid' || statusFlt === 'partial' || statusFlt === 'outstanding') {
            // Off-cycle rows have a deposit invoice; check it.
            if (!row.depositInv) return false;
            var depPaid = (paymentAllocations || []).reduce(function(s, a) {
              var t = a.documentType || a.document_type;
              var did = a.documentId != null ? a.documentId : a.document_id;
              if (t === 'invoice' && String(did) === String(row.depositInv.id)) return s + (parseFloat(a.amount) || 0);
              return s;
            }, 0);
            var dueAmt = parseFloat(row.depositInv.amountDue) || 0;
            if (statusFlt === 'outstanding' && depPaid >= dueAmt - 0.01) return false;
            if (statusFlt === 'paid'    && depPaid < dueAmt - 0.01) return false;
            if (statusFlt === 'partial' && (depPaid <= 0.01 || depPaid >= dueAmt - 0.01)) return false;
          }
        }
      }
      // Search filter
      if (q) {
        var hay;
        if (row.kind === 'bs') {
          hay = [
            row.bs.summary_ref, row.bs.summaryRef, row.bs.notes,
            row.project, monthHeadingFor(row.monthKey),
          ].filter(Boolean).join(' ').toLowerCase();
        } else {
          hay = [
            row.proposal.proposal_ref, row.proposal.proposalRef,
            row.proposal.proposal_name, row.proposal.proposalName, row.proposal.name,
            row.project, monthHeadingFor(row.monthKey),
          ].filter(Boolean).join(' ').toLowerCase();
        }
        if (hay.indexOf(q) < 0) return false;
      }
      return true;
    }

    var allRows = bsRows.concat(offCycleRows).filter(passes);

    // Step 4: group by month → project.
    var byMonth = {};
    allRows.forEach(function(r) {
      var mk = r.monthKey || 'unknown';
      if (!byMonth[mk]) byMonth[mk] = {};
      var proj = r.project || '(no project)';
      if (!byMonth[mk][proj]) byMonth[mk][proj] = [];
      byMonth[mk][proj].push(r);
    });

    // Step 5: sort months desc, projects alpha, rows within project
    // (BS first by sent_at desc, then off-cycle by approved_at desc).
    var monthKeys = Object.keys(byMonth).sort(function(a, b) {
      if (!a) return 1;
      if (!b) return -1;
      return b.localeCompare(a);
    });
    return monthKeys.map(function(mk) {
      var projGroups = Object.keys(byMonth[mk]).sort().map(function(proj) {
        var rows = byMonth[mk][proj];
        rows.sort(function(a, b) {
          // BS before off-cycle; within type, newest first
          if (a.kind !== b.kind) return a.kind === 'bs' ? -1 : 1;
          var aD = a.kind === 'bs' ? (a.sentAt || '') : (a.approvedAt || '');
          var bD = b.kind === 'bs' ? (b.sentAt || '') : (b.approvedAt || '');
          return String(bD).localeCompare(String(aD));
        });
        return { project: proj, rows: rows };
      });
      return { monthKey: mk, projects: projGroups };
    });
  }, [summaries, hoursInvoices, proposals, invoices, paymentAllocations,
      receipts, items, bsCtx, projList.join(','), periodFlt, statusFlt, query, loading]);

  // ── Floating bottom totals (scoped to filter) ────────────────────────
  var totals = useMemo(function() {
    var issued = 0, paid = 0, outstanding = 0, profit = 0;
    months.forEach(function(m) {
      m.projects.forEach(function(pg) {
        pg.rows.forEach(function(r) {
          if (r.kind === 'bs') {
            issued      += (r.rollup.totals.issued      || 0);
            paid        += (r.rollup.totals.paid        || 0);
            outstanding += (r.rollup.totals.outstanding || 0);
            // Aggregate profit across the bundled proposals
            (r.rollup.proposals || []).forEach(function(pr) {
              var itemIds = (pr.proposal && pr.proposal.items) || [];
              itemIds.forEach(function(id) {
                var it = items.find(function(x) { return x.id === id; });
                if (it) profit += parseFloat(itemTotalProfit(it, items)) || 0;
              });
            });
          } else {
            issued += r.totalIssued || 0;
            // Off-cycle deposit invoice paid amount
            if (r.depositInv) {
              var p = (paymentAllocations || []).reduce(function(s, a) {
                var t = a.documentType || a.document_type;
                var did = a.documentId != null ? a.documentId : a.document_id;
                if (t === 'invoice' && String(did) === String(r.depositInv.id)) return s + (parseFloat(a.amount) || 0);
                return s;
              }, 0);
              paid += p;
              outstanding += Math.max(0, (r.totalIssued || 0) - p);
            }
            // Profit from off-cycle proposal's items
            var ids = r.proposal.items || [];
            ids.forEach(function(id) {
              var it = items.find(function(x) { return x.id === id; });
              if (it) profit += parseFloat(itemTotalProfit(it, items)) || 0;
            });
          }
        });
      });
    });
    var rnd = function(n) { return Math.round(n * 100) / 100; };
    return { issued: rnd(issued), paid: rnd(paid), outstanding: rnd(outstanding), profit: rnd(profit) };
  }, [months, items, paymentAllocations]);

  // Bind shared payment-block lookups to this view's data.
  function clientPaymentBlocks(documentType, documentId) {
    return clientPaymentBlocksFor(documentType, documentId, paymentAllocations, clientPayments);
  }
  function vendorPaymentBlocks(poId, paymentType) {
    return vendorPaymentBlocksFor(poId, paymentType, vendorPayments);
  }

  // Row state context for procurementRowState (used in expanded
  // Proposals subsections).
  var rowCtx = useMemo(function() {
    return {
      items: items, proposals: proposals, invoices: invoices,
      paymentAllocations: paymentAllocations, vendorPayments: vendorPayments,
      purchaseOrders: purchaseOrders, payments: clientPayments,
      changeOrders: changeOrders,
    };
  }, [items, proposals, invoices, paymentAllocations, vendorPayments, purchaseOrders, clientPayments, changeOrders]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        <FilterRow
          label="Project"
          multi
          value={projList}
          options={[
            { value: '', label: 'All' },
          ].concat(allProjectLabels.map(function(lab) { return { value: lab, label: lab }; }))}
          onChange={toggleProject} />
        <FilterRow
          label="Period"
          value={periodFlt}
          options={[
            { value: '',          label: 'All' },
            { value: 'thismonth', label: 'Current month' },
            { value: 'lastmonth', label: 'Last month' },
            { value: 'last6m',    label: '6 months' },
            { value: 'lastyear',  label: 'Last year' },
          ]}
          onChange={function(v) { setFilter('period', v); }} />
        <FilterRow
          label="Status"
          value={statusFlt}
          options={[
            { value: 'all',         label: 'All' },
            { value: 'paid',        label: 'Paid' },
            { value: 'partial',     label: 'Partial' },
            { value: 'outstanding', label: 'Outstanding' },
          ]}
          onChange={function(v) { setFilter('status', v); }} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="text" placeholder="Search BS ref, proposal ref/name, notes, period…"
            value={query} onChange={function(e) { setFilter('q', e.target.value); }}
            style={{ flex: 1, minWidth: 220, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit',
              border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#fff' }} />
          <span style={{ fontSize: 11, color: '#888' }}>
            {(function() {
              var n = 0;
              months.forEach(function(m) { m.projects.forEach(function(pg) { n += pg.rows.length; }); });
              return n + ' ' + (n === 1 ? 'row' : 'rows');
            })()}
          </span>
        </div>
      </div>

      {/* Scroll container */}
      <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 6, overflow: 'auto',
        maxHeight: '72vh', background: '#fff' }}>
        {loadError && (
          <div style={{ padding: 12, color: '#C44', fontSize: 11, background: '#FFF5F5',
            borderBottom: '0.5px solid #E0E0DC' }}>
            Load failed: {loadError}
          </div>
        )}
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 12 }}>
            Loading billing summaries…
          </div>
        ) : months.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 12 }}>
            No rows match the current filters.
          </div>
        ) : months.map(function(m) {
          return (
            <div key={m.monthKey || 'unknown'} style={{ marginBottom: 18 }}>
              {/* Month header */}
              <div style={{ padding: '12px 14px 6px 14px',
                fontSize: 11, fontWeight: 700, color: '#111',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                borderBottom: '0.5px solid #D8D7D2', background: HEADER_BG,
                position: 'sticky', top: 0, zIndex: 5 }}>
                {monthHeadingFor(m.monthKey)}
              </div>
              {m.projects.map(function(pg) {
                return (
                  <div key={pg.project} style={{ padding: '0 0 12px 0' }}>
                    {/* Project header */}
                    <div style={{ padding: '8px 14px 4px 14px',
                      fontSize: 10, fontWeight: 600, color: '#444',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      background: '#FAFAF8', borderBottom: '0.5px solid #EFEFEC' }}>
                      {pg.project}
                    </div>
                    {pg.rows.map(function(r) {
                      return (
                        <RowRenderer
                          key={r.id} row={r}
                          isOpen={!!openSet[r.id]}
                          onToggle={function() { toggleOpen(r.id); }}
                          items={items}
                          invoices={invoices} hoursInvoices={hoursInvoices}
                          paymentAllocations={paymentAllocations}
                          rowCtx={rowCtx}
                          clientPaymentBlocks={clientPaymentBlocks}
                          vendorPaymentBlocks={vendorPaymentBlocks}
                          clientCreditLedger={clientCreditLedger}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Sticky bottom totals */}
        {!loading && months.length > 0 && (
          <div style={{
            position: 'sticky', bottom: 0, zIndex: 4,
            padding: '10px 14px', background: FOOTER_BG,
            borderTop: '0.5px solid #D8D7D2',
            display: 'flex', gap: 18, fontSize: 11, color: '#555',
            flexWrap: 'wrap',
          }}>
            <span>Total Issued <span style={{ fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{fmt$(totals.issued)}</span></span>
            <span>Total Paid <span style={{ fontFamily: 'monospace', color: totals.paid > 0 ? '#1A5C38' : '#888', fontWeight: 600 }}>{fmt$(totals.paid)}</span></span>
            <span>Profit <span style={{ fontFamily: 'monospace', color: '#1A5C38', fontWeight: 600 }}>{fmt$(totals.profit)}</span>
              <span style={{ color: '#aaa' }}>{' (' + (totals.issued > 0 ? Math.round((totals.profit / totals.issued) * 100) : 0) + '%)'}</span>
            </span>
            <span>Outstanding <span style={{ fontFamily: 'monospace', color: totals.outstanding > 0 ? '#D4007A' : '#888', fontWeight: 600 }}>{fmt$(totals.outstanding)}</span></span>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: '#888', marginTop: 10, lineHeight: 1.6 }}>
        Each row shows one billing summary or off-cycle proposal. Click to expand
        for line-item detail. Off-cycle proposals are Approved + have a deposit
        invoice but haven't been bundled onto a monthly billing summary.
      </div>

    </div>
  );
}

// ── RowRenderer — handles both BS and off-cycle row shapes ─────────────
function RowRenderer(p) {
  var r = p.row;
  if (r.kind === 'bs')       return <MonthlyBsRow {...p} />;
  if (r.kind === 'offcycle') return <OffCycleRow {...p} />;
  return null;
}

// ── Monthly BS row (collapsed + expanded) ───────────────────────────────
function MonthlyBsRow(p) {
  var r = p.row;
  var bs = r.bs;
  var rollup = r.rollup;
  var paid = r.paymentStatus;
  var color = amountStatusColor(paid === 'paid' ? 'paid' : paid === 'partial' ? 'partial' : 'unpaid');

  return (
    <div style={{ borderBottom: '0.5px solid #EFEFEC', background: '#fff' }}>
      <div onClick={p.onToggle}
        style={{ padding: '10px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
          background: p.isOpen ? GROUP_BG : '#fff' }}>
        <span style={{ color: '#888', fontSize: 10, width: 12 }}>
          {p.isOpen ? '\u25BE' : '\u25B8'}
        </span>
        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: '#111', minWidth: 200 }}>
          {bs.summary_ref || bs.summaryRef || bs.id.slice(0, 8)}
        </span>
        <span style={{ fontSize: 10, color: '#888', minWidth: 120 }}>
          {bs.sent_at ? ('Sent ' + fmtShortDate(bs.sent_at)) : ('Draft')}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 18, fontSize: 11, color: '#555' }}>
          <span>Issued <span style={{ fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{fmt$(rollup.totals.issued)}</span></span>
          <span>Paid <span style={{ fontFamily: 'monospace', color: rollup.totals.paid > 0 ? '#1A5C38' : '#888', fontWeight: 600 }}>{fmt$(rollup.totals.paid)}</span></span>
          <span>Outstanding <span style={{ fontFamily: 'monospace', color: rollup.totals.outstanding > 0 ? '#D4007A' : '#888', fontWeight: 600 }}>{fmt$(rollup.totals.outstanding)}</span></span>
          <span style={{ fontSize: 9, fontWeight: 600, color: color,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            padding: '1px 5px', border: '0.5px solid ' + color, borderRadius: 3, lineHeight: 1.2 }}>
            {statusLabel(paid)}
          </span>
        </span>
      </div>
      {p.isOpen && (
        <div style={{ padding: '14px 18px 18px 36px', background: '#FAFAF8',
          borderTop: '0.5px solid #E0E0DC' }}>
          <BsExpansion rollup={rollup} {...p} />
        </div>
      )}
    </div>
  );
}

// ── Off-cycle proposal row ──────────────────────────────────────────────
function OffCycleRow(p) {
  var r = p.row;
  var pp = r.proposal;
  var depInv = r.depositInv;
  // Compute paid for deposit invoice from allocations.
  var paidAmt = depInv
    ? (p.paymentAllocations || []).reduce(function(s, a) {
        var t = a.documentType || a.document_type;
        var did = a.documentId != null ? a.documentId : a.document_id;
        if (t === 'invoice' && String(did) === String(depInv.id)) return s + (parseFloat(a.amount) || 0);
        return s;
      }, 0)
    : 0;
  var amtDue = depInv ? (parseFloat(depInv.amountDue) || 0) : 0;
  var status = amtDue <= 0.01 ? 'empty'
             : paidAmt >= amtDue - 0.01 ? 'paid'
             : paidAmt > 0.01 ? 'partial'
             : 'unpaid';
  var color = amountStatusColor(status);

  return (
    <div style={{ borderBottom: '0.5px solid #EFEFEC', background: '#fff' }}>
      <div onClick={p.onToggle}
        style={{ padding: '8px 14px 8px 26px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
          background: p.isOpen ? GROUP_BG : '#fff',
          borderLeft: '2px solid #E0E0DC' }}>
        <span style={{ color: '#888', fontSize: 10, width: 12 }}>
          {p.isOpen ? '\u25BE' : '\u25B8'}
        </span>
        <span style={{ fontFamily: 'monospace', fontWeight: 500, fontSize: 11, color: '#111', minWidth: 130 }}>
          {pp.proposal_ref || pp.proposalRef || pp.id.slice(0, 8)}
        </span>
        <span style={{ fontSize: 11, color: '#222', flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pp.proposal_name || pp.proposalName || pp.name || ''}
        </span>
        <span style={{ fontSize: 9, fontWeight: 500, color: '#888',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          padding: '1px 5px', border: '0.5px solid #D8D7D2', borderRadius: 3, lineHeight: 1.2 }}>
          Off-cycle
        </span>
        <span style={{ display: 'flex', gap: 18, fontSize: 11, color: '#555' }}>
          <span>Deposit <span style={{ fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{fmt$(amtDue)}</span></span>
          {paidAmt > 0 && (
            <span>Paid <span style={{ fontFamily: 'monospace', color: '#1A5C38', fontWeight: 600 }}>{fmt$(paidAmt)}</span></span>
          )}
          <span style={{ fontSize: 9, fontWeight: 600, color: color,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            padding: '1px 5px', border: '0.5px solid ' + color, borderRadius: 3, lineHeight: 1.2 }}>
            {statusLabel(status)}
          </span>
        </span>
      </div>
      {p.isOpen && (
        <div style={{ padding: '14px 18px 18px 36px', background: '#FAFAF8',
          borderTop: '0.5px solid #E0E0DC' }}>
          <ProposalCardSection proposal={pp} {...p} />
        </div>
      )}
    </div>
  );
}

// ── BsExpansion — Design Hours / Proposals / Receipts / Credits ─────────
function BsExpansion(p) {
  var rollup = p.rollup;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DesignHoursSection rollup={rollup} {...p} />
      <ProposalsSection rollup={rollup} {...p} />
      <ReimbursableReceiptsSection rollup={rollup} {...p} />
      <CreditsSection bs={p.row.bs} {...p} />
    </div>
  );
}

function SectionHeading(p) {
  return (
    <div style={{ fontSize: 9, fontWeight: 600, color: '#666',
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
      {p.children}
    </div>
  );
}

// ── Section: Design Hours ───────────────────────────────────────────────
function DesignHoursSection(p) {
  var dh = p.rollup.designerHoursInvoice;
  if (!dh) return null;
  var color = amountStatusColor(dh.paidStatus === 'paid' ? 'paid'
    : dh.paidStatus === 'partial' ? 'partial' : 'unpaid');
  // The hours invoice ships as part of the parent billing summary, so
  // the BS's sent_at is the canonical "Payment request sent" date.
  // (hours_invoices.sent_at doesn't exist as a column in production —
  // see the load query comment for the schema-drift context.)
  var sentAt = (p.row && p.row.bs && p.row.bs.sent_at) || '';
  return (
    <div>
      <SectionHeading>Design Hours</SectionHeading>
      <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 4,
        background: '#fff', padding: '10px 12px',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontWeight: 500, color: '#111' }}>
          {dh.invoiceNumber || '\u2014'}
        </span>
        {sentAt && <span style={{ fontSize: 10, color: '#666' }}>Sent {fmtShortDate(sentAt)}</span>}
        {dh.paidAt && <span style={{ fontSize: 10, color: '#1A5C38' }}>Received {fmtShortDate(dh.paidAt)}</span>}
        <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 600,
          color: color, fontSize: 13 }}>{fmt$(dh.amount)}</span>
      </div>
    </div>
  );
}

// ── Section: Proposals (proposal cards) ─────────────────────────────────
function ProposalsSection(p) {
  var props = p.rollup.proposals || [];
  if (props.length === 0) return null;
  return (
    <div>
      <SectionHeading>Proposals</SectionHeading>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {props.map(function(pr) {
          return <ProposalCardSection key={pr.proposal.id} proposal={pr.proposal} {...p} />;
        })}
      </div>
    </div>
  );
}

// ── Section: Reimbursable Receipts ──────────────────────────────────────
function ReimbursableReceiptsSection(p) {
  var receipts = p.rollup.reimbursableReceipts || [];
  if (receipts.length === 0) return null;
  return (
    <div>
      <SectionHeading>Reimbursable Receipts</SectionHeading>
      <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 4, background: '#fff' }}>
        {receipts.map(function(rr, i) {
          var r = rr.receipt;
          return (
            <div key={r.id || i} style={{
              padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12,
              borderBottom: i < receipts.length - 1 ? '0.5px solid #EFEFEC' : 'none',
              cursor: 'pointer', fontSize: 11,
            }} onClick={function() { jumpTo('receipts', r.id); }}>
              <span style={{ minWidth: 80, fontFamily: 'monospace', color: '#666' }}>
                {fmtShortDate(r.receiptDate || r.receipt_date || r.createdAt || r.created_at)}
              </span>
              <span style={{ flex: 1, color: '#222',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(r.vendorName || r.vendor_name || '')}
                {(r.notes || r.category) ? ' — ' + (r.notes || r.category) : ''}
              </span>
              <span style={{ fontSize: 9, color: '#888', fontFamily: 'monospace' }}>
                {fmt$(rr.rawCost)} +{rr.markupPct != null ? rr.markupPct : 15}%
              </span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#111',
                minWidth: 100, textAlign: 'right' }}>
                {fmt$(rr.billedAmount)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Section: Credits ────────────────────────────────────────────────────
function CreditsSection(p) {
  var bsId = p.bs && p.bs.id;
  if (!bsId) return null;
  var entries = (p.clientCreditLedger || []).filter(function(e) {
    return e && (e.entryType === 'applied_to_billing_summary' || e.entry_type === 'applied_to_billing_summary')
      && (e.targetSummaryId === bsId || e.target_summary_id === bsId);
  });
  if (entries.length === 0) return null;
  return (
    <div>
      <SectionHeading>Credits</SectionHeading>
      <div style={{ border: '0.5px solid #E0E0DC', borderRadius: 4, background: '#fff' }}>
        {entries.map(function(e, i) {
          var amt = Math.abs(parseFloat(e.amount) || 0);
          return (
            <div key={e.id || i} style={{
              padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12,
              borderBottom: i < entries.length - 1 ? '0.5px solid #EFEFEC' : 'none',
              fontSize: 11,
            }}>
              <span style={{ minWidth: 80, fontFamily: 'monospace', color: '#666' }}>
                {fmtShortDate(e.entryDate || e.entry_date || e.createdAt || e.created_at)}
              </span>
              <span style={{ flex: 1, color: '#222',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.reference || e.id.slice(0, 8)}
              </span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1A5C38',
                minWidth: 100, textAlign: 'right' }}>
                {fmt$(amt)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ProposalCardSection — renders one proposal's banner + items table ──
// Same 9-column layout + AmountCell tooltip pattern as LedgerProcurement.
// Has its own horizontal scroll container so the wide items table fits
// inside an expanded BS row.
function ProposalCardSection(p) {
  var pp = p.proposal;
  if (!pp) return null;
  var itemIds = pp.items || [];
  var groupItems = itemIds
    .map(function(id) { return p.items.find(function(x) { return x.id === id; }); })
    .filter(function(it) {
      if (!it || it.parentId) return false;
      if (!isBillable(it)) return false;
      return true;
    });

  var groupRows = groupItems.map(function(it) { return { item: it, state: procurementRowState(it, p.rowCtx) }; });

  var totals = (function() {
    var net = 0, clientTotal = 0, depExpected = 0, depPaid = 0, outstanding = 0, profit = 0;
    groupRows.forEach(function(r) {
      net         += (r.state.net || 0);
      clientTotal += (r.state.clientTotal || 0);
      depExpected += (r.state.itemDepositExpected || 0);
      depPaid     += (r.state.itemDepositPaid || 0);
      outstanding += (r.state.clientOutstanding || 0);
      profit      += parseFloat(itemTotalProfit(r.item, p.items)) || 0;
    });
    return {
      net: Math.round(net * 100) / 100,
      clientTotal: Math.round(clientTotal * 100) / 100,
      depExpected: Math.round(depExpected * 100) / 100,
      depPaid: Math.round(depPaid * 100) / 100,
      outstanding: Math.round(outstanding * 100) / 100,
      profit: Math.round(profit * 100) / 100,
    };
  })();

  var approvedAt = pp.approved_at || pp.approvedAt || '';
  var ref  = pp.proposal_ref || pp.proposalRef || pp.id;
  var name = pp.proposal_name || pp.proposalName || pp.name || '';

  return (
    <div style={{ border: CARD_BORDER, borderRadius: CARD_RADIUS, background: '#fff' }}>
      {/* Banner */}
      <div style={{
        background: GROUP_BG,
        borderTopLeftRadius: CARD_RADIUS, borderTopRightRadius: CARD_RADIUS,
        borderBottom: '0.5px solid #D8D7D2',
        padding: '10px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
          fontSize: 12, lineHeight: 1.4 }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#111', cursor: 'pointer' }}
            onClick={function() { jumpTo('proposals', pp.id); }}>
            {ref}
          </span>
          {name && (
            <>
              <span style={{ color: '#aaa' }}>{'\u00b7'}</span>
              <span style={{ color: '#222' }}>{name}</span>
            </>
          )}
          {approvedAt && (
            <>
              <span style={{ color: '#aaa' }}>{'\u00b7'}</span>
              <span style={{ fontSize: 10, color: '#1A5C38' }}>Approved {fmtShortDate(approvedAt)}</span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 4, fontSize: 10, color: '#555',
          flexWrap: 'wrap' }}>
          <span>Net <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(totals.net)}</span></span>
          <span>Client <span style={{ fontFamily: 'monospace', color: '#111' }}>{fmt$(totals.clientTotal)}</span></span>
          <span>Profit <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(totals.profit)}</span><span style={{ color: '#aaa' }}>{' (' + (totals.net > 0 ? Math.round((totals.profit / totals.net) * 100) : 0) + '%)'}</span></span>
          <span>Dep collected <span style={{ fontFamily: 'monospace', color: '#1A5C38' }}>{fmt$(totals.depPaid)}</span><span style={{ color: '#aaa' }}> / {fmt$(totals.depExpected)}</span></span>
          <span>Outstanding <span style={{ fontFamily: 'monospace',
            color: totals.outstanding > 0 ? '#D4007A' : '#888' }}>{fmt$(totals.outstanding)}</span></span>
        </div>
      </div>
      {/* Items table — own horizontal scroll so wide content fits */}
      <div style={{ overflowX: 'auto' }}>
        <ProposalItemsTable groupRows={groupRows} {...p} />
      </div>
    </div>
  );
}

function ProposalItemsTable(p) {
  return (
    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: 'max-content' }}>
      <ColGroup />
      <thead>
        <tr>
          <th style={Object.assign({}, th, cellWidth(W.product))}>Product</th>
          <th style={Object.assign({}, thRight, cellWidth(W.deposit))}>Client Deposit</th>
          <th style={Object.assign({}, thRight, cellWidth(W.final))}>Client Final</th>
          <th style={Object.assign({}, thRight, cellWidth(W.po))}>Vendor PO</th>
          <th style={Object.assign({}, thRight, cellWidth(W.venFinal))}>Vendor Final</th>
          <th style={Object.assign({}, th, cellWidth(W.delivered))}>Delivered</th>
          <th style={Object.assign({}, thRight, cellWidth(W.fees))}>Fees</th>
          <th style={Object.assign({}, thRight, cellWidth(W.cliOut))}>Receivable</th>
          <th style={Object.assign({}, thRight, cellWidth(W.venOut))}>Payable</th>
        </tr>
      </thead>
      <tbody>
        {p.groupRows.map(function(r, ri) {
          return <ItemRow key={r.item.id} rowData={r} ri={ri} {...p} />;
        })}
      </tbody>
    </table>
  );
}

// ── ItemRow — one row in a proposal card's items table ────────────────
// Mirrors the per-row rendering from LedgerProcurement (same cells, same
// tooltips).
function ItemRow(p) {
  var it = p.rowData.item;
  var st = p.rowData.state;
  var rowBg = p.ri % 2 === 0 ? ROW_BG_A : ROW_BG_B;

  // Compute per-cell state
  var depSt = depositState(st);
  var finSt = finalState(st);
  var poSt  = poState(st, it);
  var venSt = venFinalState(st);

  // DEPOSIT cell content
  var depAmount = null, depPartialOf = null;
  if (depSt === 'paid')      depAmount = st.itemDepositPaid;
  else if (depSt === 'partial') { depAmount = st.itemDepositPaid; depPartialOf = st.itemDepositExpected; }
  else if (depSt === 'unpaid' || depSt === 'projected') depAmount = st.itemDepositExpected;

  var depPaymentBlocks = st.depositInvoice
    ? p.clientPaymentBlocks('invoice', st.depositInvoice.invoice.id) : [];
  var depTooltip = st.depositInvoice ? (
    <div>
      <TooltipHeading>Deposit Invoice</TooltipHeading>
      <TooltipRow label="Ref" mono value={st.depositInvoice.invoiceRef || '\u2014'}
        onClick={function() { jumpTo('invoices', st.depositInvoice.invoice.id); }} />
      {st.depositInvoice.sentAt && <TooltipRow label="Sent" value={fmtShortDate(st.depositInvoice.sentAt)} />}
      <TooltipRow label="Client deposit (req'd)" mono value={fmt$(st.itemDepositExpected)} />
      {st.itemDepositPaid > 0 && <TooltipRow label="Client deposit paid" mono value={fmt$(st.itemDepositPaid)} />}
      <TooltipRow label="Status" value={statusLabel(depSt)} />
      {PaymentBlocks(depPaymentBlocks)}
    </div>
  ) : null;

  // PO cell
  var poAmount = st.po ? st.net : null;
  var poDepositBlocks = st.po
    ? p.vendorPaymentBlocks(st.po.id, 'Deposit')
        .concat(p.vendorPaymentBlocks(st.po.id).filter(function(b) {
          return b.paymentType === 'Deposit Payment' || b.paymentType === 'Full';
        }))
    : [];
  var venDepExpected = st.po
    ? Math.round((parseFloat(it.netPrice) || 0) * (parseInt(it.qty, 10) || 1) * (parseFloat(it.depositPct) || 50) / 100 * 100) / 100
    : 0;
  var poTooltip = st.po ? (
    <div>
      <TooltipHeading>Purchase Order</TooltipHeading>
      <TooltipRow label="Ref" mono value={st.po.ref || '\u2014'}
        onClick={function() { jumpTo('purchase-orders', st.po.id); }} />
      {st.po.sentAt && <TooltipRow label="Issued" value={fmtShortDate(st.po.sentAt)} />}
      <TooltipRow label="Item net" mono value={fmt$(st.net)} />
      {venDepExpected > 0 && <TooltipRow label="Vendor deposit (req'd)" mono value={fmt$(venDepExpected)} />}
      {st.itemVendorDepositPaid > 0 && <TooltipRow label="Vendor deposit paid" mono value={fmt$(st.itemVendorDepositPaid)} />}
      <TooltipRow label="Status" value={statusLabel(poSt)} />
      {PaymentBlocks(poDepositBlocks)}
    </div>
  ) : null;

  // FINAL cell
  var finAmount = null, finPartialOf = null;
  if (finSt === 'paid')      finAmount = st.itemFinalPaid;
  else if (finSt === 'partial') { finAmount = st.itemFinalPaid; finPartialOf = st.itemFinalExpected; }
  else if (finSt === 'unpaid' || finSt === 'projected') finAmount = st.itemFinalExpected;

  var finPaymentBlocks = st.finalInvoice
    ? p.clientPaymentBlocks('invoice', st.finalInvoice.invoice.id) : [];
  var finTooltip = st.finalInvoice ? (
    <div>
      <TooltipHeading>Final Invoice</TooltipHeading>
      <TooltipRow label="Ref" mono value={st.finalInvoice.invoiceRef || '\u2014'}
        onClick={function() { jumpTo('invoices', st.finalInvoice.invoice.id); }} />
      {st.finalInvoice.sentAt && <TooltipRow label="Sent" value={fmtShortDate(st.finalInvoice.sentAt)} />}
      <TooltipRow label="Client final (req'd)" mono value={fmt$(st.itemFinalExpected)} />
      {st.itemFinalPaid > 0 && <TooltipRow label="Client final paid" mono value={fmt$(st.itemFinalPaid)} />}
      <TooltipRow label="Status" value={statusLabel(finSt)} />
      {PaymentBlocks(finPaymentBlocks)}
    </div>
  ) : (st.itemFinalExpected > 0.01 ? (
    <div>
      <TooltipHeading>Final (Projected)</TooltipHeading>
      <TooltipRow label="Projected" mono value={fmt$(st.itemFinalExpected)} />
    </div>
  ) : null);

  // VENDOR FINAL
  var venFinProjected = Math.max(0, Math.round(((st.net || 0) - (st.itemVendorDepositPaid || 0)) * 100) / 100);
  var venFinAmount = venSt === 'paid' ? st.itemVendorFinalPaid
                   : venSt === 'projected' ? venFinProjected : null;
  var venFinBlocks = st.po ? p.vendorPaymentBlocks(st.po.id, 'Final') : [];
  var venFinTooltip = venSt === 'paid' ? (
    <div>
      <TooltipHeading>Vendor Final Payment</TooltipHeading>
      <TooltipRow label="Item share" mono value={fmt$(st.itemVendorFinalPaid)} />
      {PaymentBlocks(venFinBlocks)}
    </div>
  ) : venSt === 'projected' ? (
    <div>
      <TooltipHeading>Vendor Final (Projected)</TooltipHeading>
      <TooltipRow label="Item PO contribution" mono value={fmt$(st.net || 0)} />
      <TooltipRow label="Vendor deposit paid" mono value={fmt$(st.itemVendorDepositPaid || 0)} />
      <TooltipRow label="Projected final" mono value={fmt$(venFinProjected)} />
    </div>
  ) : null;

  return (
    <tr style={{ background: rowBg }}>
      {/* PRODUCT */}
      <td style={Object.assign({}, td, cellWidth(W.product), { cursor: 'pointer', overflow: 'hidden' })}
        onClick={function() { jumpTo('items', it.id); }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <div style={{ fontSize: 12, color: '#111', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
            {it.productName || it.item || '(unnamed)'}
          </div>
          <LedgerCoBadge changeOrders={st.changeOrders} />
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#888', marginTop: 2 }}>
          {it.itemNumber || '\u2014'}
        </div>
      </td>
      {/* CLIENT DEPOSIT */}
      <td style={Object.assign({}, td, cellWidth(W.deposit))}>
        <AmountCell amount={depAmount} state={depSt} partialOf={depPartialOf}
          align="right" tooltip={depTooltip} />
      </td>
      {/* CLIENT FINAL */}
      <td style={Object.assign({}, td, cellWidth(W.final))}>
        <AmountCell amount={finAmount} state={finSt} partialOf={finPartialOf}
          align="right" tooltip={finTooltip} />
      </td>
      {/* VENDOR PO */}
      <td style={Object.assign({}, td, cellWidth(W.po))}>
        <AmountCell amount={poAmount} state={poSt}
          align="right" tooltip={poTooltip} />
      </td>
      {/* VENDOR FINAL */}
      <td style={Object.assign({}, td, cellWidth(W.venFinal))}>
        <AmountCell amount={venFinAmount} state={venSt}
          align="right" tooltip={venFinTooltip} />
      </td>
      {/* DELIVERED */}
      <td style={Object.assign({}, td, cellWidth(W.delivered))}>
        {st.receivedDate ? (
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#111' }}>
            {fmtShortDate(st.receivedDate)}
          </span>
        ) : (
          <span style={{ color: '#bbb', fontFamily: 'monospace', fontSize: 14 }}>{'\u2014'}</span>
        )}
      </td>
      {/* FEES */}
      <td style={Object.assign({}, td, cellWidth(W.fees), { textAlign: 'right' })}>
        <FeesCell item={it} total={st.itemFees} />
      </td>
      {/* RECEIVABLE */}
      <td style={Object.assign({}, td, cellWidth(W.cliOut),
        { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: 13,
          color: st.clientOutstanding > 0 ? '#D4007A' : '#888' })}>
        {fmt$(st.clientOutstanding)}
      </td>
      {/* PAYABLE */}
      <td style={Object.assign({}, td, cellWidth(W.venOut),
        { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, fontSize: 13,
          color: st.vendorOutstanding > 0 ? '#BF6800' : '#888' })}>
        {fmt$(st.vendorOutstanding)}
      </td>
    </tr>
  );
}
